import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { routes } from './constants/routes';
import { ProtectedRoute } from './view/components/privateRoute';
import { useAppDispatch, useAppSelector } from './utils/reduxHooks';
import { SignInPage } from './view/pages/Login';
import { SignUpPage } from './view/pages/Registration';
import { selectIsLoadingApp, selectIsLoggedApp } from './modules/app/selectors';
import { Loader } from './view/components/UI';
import { NotFoundPage } from './view/pages/NotFound';
import { getUserAction } from './modules/user/actions';
import { CatalogPage } from './view/pages/Catalog';
import AuthLayout from './view/components/layouts/auth';
import { ClientPageLayout } from './view/components/layouts/catalog';
import ReturnsPage from './view/pages/ReturnsPage';
import { PasswordRecoveryPage } from './view/pages/PasswordRecovery';
import { CartPage } from './view/pages/Cart';
import { TransactionHistory } from './view/pages/TransactionHistory';
import { AccountSettings } from './view/pages/Settings';
import OrdersPage from './view/pages/Orders';
import { Groups } from './view/pages/Catalog/Groups';
import { Scheme } from './view/pages/Catalog/Scheme';
import { ModelSelection } from './view/pages/Catalog/ModelSelection';
import { ModificationsList } from './view/pages/Catalog/ModificationsList';
import { FiltersPage } from './view/pages/Catalog/Filters';
import { selectUserFirstName } from './modules/user/selectors';

export const App = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = !!useAppSelector(selectIsLoggedApp);
  const isAppLoading = useAppSelector(selectIsLoadingApp);
  const firstName = useAppSelector(selectUserFirstName);

  // const isAuthenticated = !!TokenService.getAccessToken();

  useEffect(() => {
    if (!isAuthenticated || !firstName) {
      dispatch(getUserAction.request());
    }
  }, [isAuthenticated, firstName]);

  if (isAppLoading) {
    return <Loader message="Loading" />;
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route
          path={routes.SIGN_IN}
          element={
            <AuthLayout>
              <ProtectedRoute isAuthenticated={!isAuthenticated} redirectTo={routes.MAIN}>
                <SignInPage />
              </ProtectedRoute>
            </AuthLayout>
          }
        />
        <Route
          path={routes.SIGN_UP}
          element={
            <AuthLayout>
              <ProtectedRoute isAuthenticated={!isAuthenticated} redirectTo={routes.MAIN}>
                <SignUpPage />
              </ProtectedRoute>
            </AuthLayout>
          }
        />
        <Route
          path={routes.PASSWORD_RECOVERY}
          element={
            <AuthLayout>
              <ProtectedRoute isAuthenticated={!isAuthenticated} redirectTo={routes.MAIN}>
                <PasswordRecoveryPage />
              </ProtectedRoute>
            </AuthLayout>
          }
        />

        {/* Protected Routes */}
        <Route
          path={routes.MAIN}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} redirectTo={routes.SIGN_IN}>
              <ClientPageLayout>
                <CatalogPage />
              </ClientPageLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={routes.CATALOG_MODEL}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} redirectTo={routes.SIGN_IN}>
              <ClientPageLayout>
                <ModelSelection />
              </ClientPageLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path={routes.CATALOG_MODIFICATION}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} redirectTo={routes.SIGN_IN}>
              <ClientPageLayout>
                <ModificationsList />
              </ClientPageLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={routes.CATALOG_GROUPS}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} redirectTo={routes.SIGN_IN}>
              <ClientPageLayout>
                <Groups />
              </ClientPageLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={routes.CATALOG_SCHEME}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} redirectTo={routes.SIGN_IN}>
              <ClientPageLayout>
                <Scheme />
              </ClientPageLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={routes.CATALOG_FILTERS}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} redirectTo={routes.SIGN_IN}>
              <ClientPageLayout>
                <FiltersPage />
              </ClientPageLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={routes.CATALOG_SEARCH_BAR_RESULTS}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} redirectTo={routes.SIGN_IN}>
              <ClientPageLayout>
                <FiltersPage />
              </ClientPageLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={routes.CATALOG_CART}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} redirectTo={routes.SIGN_IN}>
              <ClientPageLayout>
                <CartPage />
              </ClientPageLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={routes.RETURNS}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} redirectTo={routes.SIGN_IN}>
              <ClientPageLayout>
                <ReturnsPage />
              </ClientPageLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={routes.TRANSACTIONS_HISTORY}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} redirectTo={routes.SIGN_IN}>
              <ClientPageLayout>
                <TransactionHistory />
              </ClientPageLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={routes.SETTINGS}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} redirectTo={routes.SIGN_IN}>
              <ClientPageLayout>
                <AccountSettings />
              </ClientPageLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path={routes.ORDERS}
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} redirectTo={routes.SIGN_IN}>
              <ClientPageLayout>
                <OrdersPage />
              </ClientPageLayout>
            </ProtectedRoute>
          }
        />

        {/* Page Not Found Route */}
        <Route path={routes.PAGE_NOT_FOUND} element={<NotFoundPage />} />

        {/* Redirect to Page Not Found for unmatched routes */}
        <Route path="*" element={<Navigate to={routes.MAIN} />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
